import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLinkIcon, Globe2Icon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/Button';
import { WebsiteEditor } from '../components/website/WebsiteEditor';
import { PublishBar } from '../components/website/PublishBar';
import { describeDraftChanges, discardWebsiteDraft, getDraftWebsiteContent, getLastPublishedAt, hasUnpublishedChanges, publishWebsiteContent, saveDraftWebsiteContent, websiteCloudLoadedEvent, type WebsiteContent } from '../data/website';
export function WebsiteStudio() {
  const [draft, setDraft] = useState<WebsiteContent>(() => getDraftWebsiteContent());
  const [lastPublishedAt, setLastPublishedAt] = useState<string | null>(() => getLastPublishedAt());
  const [justPublished, setJustPublished] = useState(false);
  const [error, setError] = useState('');
  // Bumped when the cloud hands down a newer version, so the editor re-seeds
  // from it rather than continuing to edit a stale local copy.
  const [cloudVersion, setCloudVersion] = useState(0);
  useEffect(() => {
    const adopt = (event: Event) => {
      const incoming = (event as CustomEvent<WebsiteContent>).detail;
      if (!incoming) return;
      setDraft(incoming);
      setLastPublishedAt(getLastPublishedAt());
      setCloudVersion(version => version + 1);
    };
    window.addEventListener(websiteCloudLoadedEvent, adopt as EventListener);
    return () => window.removeEventListener(websiteCloudLoadedEvent, adopt as EventListener);
  }, []);
  const changedSections = describeDraftChanges(draft);
  const changed = hasUnpublishedChanges(draft);
  useEffect(() => {
    if (!justPublished) return undefined;
    const timer = window.setTimeout(() => setJustPublished(false), 2400);
    return () => window.clearTimeout(timer);
  }, [justPublished]);

  /** Every keystroke lands in the draft. Fans keep seeing the live version. */
  const saveDraft = useCallback((content: WebsiteContent) => {
    const stored = saveDraftWebsiteContent(content);
    setDraft(content);
    setError(stored ? '' : 'Couldn’t save this change on this device. Check the cloud status below.');
    return stored;
  }, []);
  const publish = () => {
    if (!publishWebsiteContent(draft)) {
      setError('Couldn’t publish from this device. Check the cloud status below and try again.');
      return;
    }
    setLastPublishedAt(getLastPublishedAt());
    setJustPublished(true);
    setError('');
  };
  const discard = () => {
    setDraft(discardWebsiteDraft());
    setError('');
  };
  return <div data-id="element-1839" data-mp="1p4pjp4egw18k">
      <PageHeader eyebrow="Website Studio" title="Draft it, check it, then publish." subtitle="Edits are saved to a private draft. Preview the real site from that draft, and publish only when it looks right." actions={<a href="/" target="_blank" rel="noreferrer" data-id="element-1841" data-mp="1k4b8jdhkkb6b">
            <Button variant="secondary" size="sm" data-id="element-1842" data-mp="1k5w496fme05r">
              <Globe2Icon className="h-4 w-4" data-id="element-1843" data-mp="10jazz34ujrir" /> View live site <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-1844" data-mp="10k2wqha6jvam" />
            </Button>
          </a>} data-id="element-1840" data-mp="1p4z7clc1hwb3" />

      <div className="mb-4" data-id="element-1845" data-mp="1agfpncxwi54b">
        <PublishBar hasChanges={changed} changedSections={changedSections} lastPublishedAt={lastPublishedAt} justPublished={justPublished} onPublish={publish} onDiscard={discard} data-id="element-1846" data-mp="1agphl62iamcn" />
      </div>

      {error && <div role="alert" className="mb-4 rounded-xl border border-danger/25 bg-danger/10 px-3 py-2.5 text-[12px] text-rose-200" data-id="element-1847" data-mp="1eiao89og64ee">
          {error}
        </div>}

      <WebsiteEditor key={cloudVersion} initialContent={draft} onPublish={saveDraft} data-id="element-1848" data-mp="1t901ripdrvlf" />
    </div>;
}